import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import { usePerspective } from "../lib/perspective";
import FokusHeute from "../components/FokusHeute";
import NextActions from "../components/NextActions";
import { dashboardUrl, DASHBOARDS, type DashboardKey } from "../lib/deeplinks";
import "../styles.launcher.css";

/**
 * Single Pane of Glass / App-Launcher (Welle 1, BEN-104/105).
 * EINE zentrale Startsicht, die alle relevanten Systeme "aus einem Guss" zusammenfuehrt:
 *   1) kpi-Module (interne Ansichten)   2) OpenSearch-Dashboards (Kachel + Deep-Link, neuer Tab)
 *   3) Fachsysteme (OpenProject, KIM-Chatbot, Nextcloud, 1und1os) als Deep-Links.
 * Rollenbewusst: Kunde sieht die verdichtete Auswahl, Berater die volle Steuerung.
 * Dashboards werden bewusst NICHT als iframe eingebettet (SSO/Session-Schutz), sondern
 * als Kachel mit Live-Kennzahl + Deep-Link geoeffnet.
 */

type NavBadgeCounts = {
  kpi_count?: number; kpi_kritisch?: number; use_case_count?: number;
  runs_count?: number; wp_offen?: number | null;
  prozess_erledigt?: number; prozess_gesamt?: number;
};

const SYS = {
  openproject: "https://project.impulse.kim/projects",
  chatbot: "https://1und1-chat.ki-guru.com",
  nextcloud: "https://cloud.impulse.kim",
  portal: "https://1und1os.impulse.kim",
  discover: "https://opensearch.ben-e-fit.ai/app/data-explorer/discover",
};

type Tile = {
  key: string; title: string; desc: string; icon: string;
  to?: string; href?: string; kind: "intern" | "dashboard" | "extern";
  metric?: string; badge?: string; beraterOnly?: boolean; hero?: boolean;
};

function LaunchCard({ t }: { t: Tile }) {
  const external = t.kind !== "intern";
  const cls = `lp-card kind-${t.kind}${t.hero ? " lp-hero-card" : ""}`;
  const inner = (
    <>
      {t.badge && <span className="lp-badge">{t.badge}</span>}
      <div className="lp-card-top">
        <span className="lp-card-ic" aria-hidden>{t.icon}</span>
        <span className="lp-card-title">{t.title}</span>
      </div>
      <span className="lp-card-desc">{t.desc}</span>
      <span className="lp-card-foot">
        <span className="lp-card-metric">{t.metric ?? ""}</span>
        <span className="lp-open">Öffnen{external ? " ↗" : " →"}</span>
      </span>
    </>
  );
  if (external && t.href) {
    return <a className={cls} href={t.href} target="_blank" rel="noopener noreferrer" title={t.desc}>{inner}</a>;
  }
  if (t.to) {
    return <Link className={cls} to={t.to} title={t.desc}>{inner}</Link>;
  }
  return null;
}

export default function StartPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const { isBerater, perspective } = usePerspective();

  const badges = useQuery({
    queryKey: ["start-badges", effectiveTenantId],
    queryFn: () => api.get<NavBadgeCounts>("/nav/badges"),
    retry: 1,
  });
  const d: NavBadgeCounts = badges.data ?? {};

  const decStats = useQuery({
    queryKey: ["start-dec-stats", effectiveTenantId],
    queryFn: () => api.get<{ offen: number; total: number }>("/decisions/stats"),
    retry: 1, staleTime: 30_000,
  });
  const mtgStats = useQuery({
    queryKey: ["start-mtg-stats", effectiveTenantId],
    queryFn: () => api.get<{ meetings: number }>("/meetings/stats"),
    retry: 1, staleTime: 60_000,
  });
  const arcStats = useQuery({
    queryKey: ["start-arc-stats", effectiveTenantId],
    queryFn: () => api.get<{ total: number; abgeschlossen: number }>("/ai-ready-check/stats"),
    retry: 1, staleTime: 60_000,
  });
  const kbStats = useQuery({
    queryKey: ["start-kb-stats", effectiveTenantId],
    queryFn: () => api.get<{ own_total: number; overdue: number; approvals_open: number }>("/kanban/stats"),
    retry: 1, staleTime: 60_000,
  });
  const formStats = useQuery({
    queryKey: ["start-form-stats", effectiveTenantId],
    queryFn: () => api.get<{ total: number; pending_assigned: number }>("/forms/stats"),
    retry: 1, staleTime: 60_000,
  });
  const controlStats = useQuery({
    queryKey: ["start-control-system", effectiveTenantId],
    queryFn: () => api.get<{ decision_queue: { open: number }; cases: { live: number }; kpis: { incomplete: number } }>("/control-system/overview"),
    retry: 1, staleTime: 30_000,
  });

  const prozess = (d.prozess_gesamt ?? 0) > 0 ? `${d.prozess_erledigt ?? 0}/${d.prozess_gesamt} Schritte` : undefined;
  const krit = (d.kpi_kritisch ?? 0) > 0 ? `${d.kpi_kritisch} KPI kritisch` : undefined;

  const heroAmpel = (() => {
    if ((d.kpi_kritisch ?? 0) > 0) return { cls: "rot", label: "Handlungsbedarf", why: `${d.kpi_kritisch} KPI kritisch` };
    if ((d.prozess_gesamt ?? 0) > 0) return { cls: "gelb", label: "Im Aufbau", why: `${d.prozess_erledigt ?? 0}/${d.prozess_gesamt} Prozessschritte erledigt` };
    return { cls: "grau", label: "Übersicht", why: "Alle Systeme aus einem Guss" };
  })();

  const module: Tile[] = [
    { key: "control-system", to: "/app/steuerungssystem", kind: "intern", icon: "↻", badge: "Neu",
      title: "Steuerungssystem", desc: "Priorisierte Entscheidungen, Maßnahmen, Wirkung und nächster Messzyklus.",
      metric: (controlStats.data?.decision_queue.open ?? 0) > 0 ? `${controlStats.data?.decision_queue.open} Entscheidungen offen` : ((controlStats.data?.cases.live ?? 0) > 0 ? `${controlStats.data?.cases.live} Fälle live` : undefined) },
    { key: "steuerkreis", to: "/app/steuerkreis", kind: "intern", icon: "◎", hero: true,
      title: "Steuerkreis-Cockpit", desc: "Ampeln, Gate-Matrix, KI-Briefing und „Frag die Daten“ auf einen Blick.", metric: krit ?? prozess },
    { key: "kundencockpit", to: "/app/kundencockpit", kind: "intern", icon: "◧", badge: "Neu",
      title: "Kundencockpit", desc: "Verdichtete Management-/Kundensicht auf einen Blick — Programm-Ampel, Gate-Matrix, KPIs. Beamer-tauglich für Steuerkreis & Kundentermin.", metric: krit ?? prozess },
    { key: "prozess", to: "/app/process-landscape", kind: "intern", icon: "▤", beraterOnly: true,
      title: "Prozesslandschaft", desc: "Geführte Prozessschritte: Eingaben, Wissensbasis, KI-Analyse, Nachweis, Wirkung.", metric: prozess },
    { key: "interventionen", to: "/app/interventionen", kind: "intern", icon: "✦", beraterOnly: true, badge: "72",
      title: "Interventionskatalog", desc: "Situation → passende Intervention, exakte Datensätze inkl. rechtlicher Vorbehalte." },
    { key: "trees", to: "/app/trees", kind: "intern", icon: "⋔",
      title: "Treiberbäume", desc: "Wirkungslogik Ursache → Wirkung je KPI." },
    { key: "roi", to: "/app/roi", kind: "intern", icon: "∿", beraterOnly: true,
      title: "ROI / Monte-Carlo", desc: "Business Case und Szenarien (P10/P50/P90) statt Wunschzahl." },
    { key: "usecases", to: "/app/use-cases", kind: "intern", icon: "◈", beraterOnly: true,
      title: "KI Use Cases", desc: "Impact, Readiness und Risiko der 1&1-Anwendungsfälle.", metric: (d.use_case_count ?? 0) > 0 ? `${d.use_case_count} Use Cases` : undefined },
    { key: "aiready", to: "/app/ai-ready-check", kind: "intern", icon: "✵", beraterOnly: true, badge: "Neu",
      title: "AI-Ready-Check", desc: "Reifegrad-Assessment (Daten, Governance, Use Cases, Skills, Infrastruktur, Change) mit Empfehlungen & Interventionen.",
      metric: (arcStats.data?.abgeschlossen ?? 0) > 0 ? `${arcStats.data?.abgeschlossen} abgeschlossen` : ((arcStats.data?.total ?? 0) > 0 ? `${arcStats.data?.total} angelegt` : undefined) },
    { key: "advisor", to: "/app/advisor", kind: "intern", icon: "✳", beraterOnly: true,
      title: "KI-Berater", desc: "Belegte Antworten aus der 1&1-Wissensbasis (grounded, mit Quellen-Chips)." },
    { key: "briefing", to: "/app/briefing-agent", kind: "intern", icon: "▦",
      title: "Management-Briefing", desc: "Lokales Modell schreibt ein belegtes Steuerungsbriefing — exportierbar." },
    { key: "meetings", to: "/app/meetings", kind: "intern", icon: "❒", badge: "Neu",
      title: "Meeting-Cockpit", desc: "KI-Zusammenfassung & Action-Items aus echten Protokollen — grounded, mit Quellenbeleg.",
      metric: (mtgStats.data?.meetings ?? 0) > 0 ? `${mtgStats.data?.meetings} Meetings` : undefined },
    { key: "decisions", to: "/app/decisions", kind: "intern", icon: "⚖", badge: "Neu",
      title: "Entscheidungs-Log", desc: "Entscheidungen erfassen, begründen, verknüpfen und nachverfolgen.",
      metric: (decStats.data?.offen ?? 0) > 0 ? `${decStats.data?.offen} offen` : undefined },
    { key: "kanban", to: "/app/kanban", kind: "intern", icon: "▦", badge: "Neu",
      title: "Kanban-Board", desc: "Ressort-Swimlanes, WIP-Limits und belegte Freigaben für OpenProject-Statuswechsel.",
      metric: (kbStats.data?.approvals_open ?? 0) > 0 ? `${kbStats.data?.approvals_open} Freigaben offen` : ((kbStats.data?.overdue ?? 0) > 0 ? `${kbStats.data?.overdue} überfällig` : ((kbStats.data?.own_total ?? 0) > 0 ? `${kbStats.data?.own_total} eigene Karten` : undefined)) },
    { key: "formulare", to: "/app/formulare", kind: "intern", icon: "❏", badge: "Neu",
      title: "Formulare", desc: "Grounded Methoden-Vorlagen, Zuweisungen, Antwortauswertung und CSV-Export.",
      metric: (formStats.data?.pending_assigned ?? 0) > 0 ? `${formStats.data?.pending_assigned} offen` : ((formStats.data?.total ?? 0) > 0 ? `${formStats.data?.total} Formulare` : undefined) },
  ];

  const dashList: { key: DashboardKey; icon: string; metric?: string; beraterOnly?: boolean }[] = [
    { key: "cockpit", icon: "▦", metric: (d.kpi_count ?? 0) > 0 ? `${d.kpi_count} KPIs` : undefined },
    { key: "oe-board", icon: "▤", metric: "7 Ressorts × 9 Phasen" },
    { key: "steuerung-wirkung", icon: "◫", metric: (d.runs_count ?? 0) > 0 ? `${d.runs_count} Runs` : undefined },
    { key: "systemgesundheit", icon: "♥", beraterOnly: true },
  ];

  const systeme: Tile[] = [
    { key: "portal", href: SYS.portal, kind: "extern", icon: "⬡",
      title: "1und1os-Portal", desc: "Einstieg ins KI-Betriebssystem: KIM-Chat, Apps, Agent-Room." },
    { key: "openproject", href: SYS.openproject, kind: "extern", icon: "▣",
      title: "OpenProject", desc: "Arbeitspakete & Teilprojekte des 1&1-Programms (Maßnahmen, Termine).", metric: (d.wp_offen ?? 0) > 0 ? `${d.wp_offen} WP offen` : undefined },
    { key: "chatbot", href: SYS.chatbot, kind: "extern", icon: "✺",
      title: "1&1-KIM-Chatbot", desc: "Fragen an die 1&1-Wissensbasis & KPIs im Chat (gleiche Datenbasis)." },
    { key: "nextcloud", href: SYS.nextcloud, kind: "extern", icon: "☁",
      title: "Nextcloud", desc: "Dateiablage 1und1KIM_OS: Protokolle, Decks, Contracting-Dokumente." },
    { key: "discover", href: SYS.discover, kind: "extern", icon: "⌕", beraterOnly: true,
      title: "OpenSearch Discover", desc: "Volltextsuche über alle 1&1-Indizes (Dokumente & Arbeitspakete)." },
  ];

  const show = (t: { beraterOnly?: boolean }) => isBerater || !t.beraterOnly;

  return (
    <section className="launcher">
      <header className="launcher-hero">
        <div>
          <p className="hero-eyebrow">Single Pane of Glass · {perspective === "berater" ? "Beratersicht (Impulse)" : "Managementsicht (1&1)"}</p>
          <h1>1&amp;1 Transformation — alles aus einem Guss</h1>
          <p className="hero-sub">
            Ein Einstieg für alles: die Steuerungs- und Analyse-Module des Cockpits, die OpenSearch-Dashboards
            und die verbundenen Fachsysteme. Jede Kachel erklärt, was Sie dort finden — keine toten Enden, ein Klick genügt.
          </p>
        </div>
        <div className="launcher-hero-cta">
          <div className={`launcher-ampel ${heroAmpel.cls}`} role="status" aria-label={`Programm-Status: ${heroAmpel.label}`}>
            <span className="amp-dot" aria-hidden />
            <span className="amp-copy">
              <span className="amp-k">Programm-Status</span>
              <strong>{heroAmpel.label}</strong>
              <span>{heroAmpel.why}</span>
            </span>
          </div>
          <Link className="lp-open" to="/app/steuerkreis">Zum Steuerkreis-Cockpit →</Link>
        </div>
      </header>

      <FokusHeute role={perspective} />

      <NextActions role={perspective} limit={6} />

      <div className="lp-section">
        <div className="lp-section-head">
          <h2>Steuerung &amp; Analyse</h2>
          <span className="lp-section-sub">Die Module des Cockpits — {perspective === "berater" ? "volle Steuerung" : "verdichtete Sicht"}.</span>
        </div>
        <div className="lp-grid">
          {module.filter(show).map((t) => <LaunchCard key={t.key} t={t} />)}
        </div>
      </div>

      <div className="lp-section">
        <div className="lp-section-head">
          <h2>Dashboards (OpenSearch)</h2>
          <span className="lp-section-sub">Detail-Dashboards mit Live-Daten.</span>
        </div>
        <div className="lp-grid">
          {dashList.filter(show).map((t) => {
            const meta = DASHBOARDS[t.key];
            const tile: Tile = { key: t.key, href: dashboardUrl(t.key), kind: "dashboard", icon: t.icon, title: meta.label, desc: meta.desc, metric: t.metric };
            return <LaunchCard key={t.key} t={tile} />;
          })}
        </div>
        <p className="lp-note">
          Die Dashboards öffnen in einem neuen Tab mit Ihrer bestehenden sicheren Anmeldung. Aus Sicherheitsgründen
          (Schutz der SSO-Sitzung) werden sie bewusst nicht als eingebettetes Fenster (iframe) angezeigt — so bleibt
          Ihre Anmeldung geschützt und die Ansicht in voller Größe nutzbar.
        </p>
      </div>

      <div className="lp-section">
        <div className="lp-section-head">
          <h2>Fachsysteme</h2>
          <span className="lp-section-sub">Direkt in die verbundenen Systeme springen (neuer Tab, Ihre Anmeldung gilt).</span>
        </div>
        <div className="lp-grid">
          {systeme.filter(show).map((t) => <LaunchCard key={t.key} t={t} />)}
        </div>
      </div>

      <footer className="sk-footer muted small">
        Ein Guss statt Tool-Zoo · jede Kachel erklärt ihren Zweck · Deep-Links öffnen mit Ihrer bestehenden Anmeldung ·
        Dashboards &amp; Fachsysteme sicher verlinkt, nicht eingebettet.
      </footer>
    </section>
  );
}
